import { useMemo, useState } from "react";
import { cn } from "@shared/lib/utils";
import { AgentAvatar } from "@/components/collection/AgentAvatar";
import { useCollectionData } from "@/components/collection/useCollectionData";
import { CARD_FINISHES } from "@/lib/cards";
import { HOME_AGENTS, type HomeAgent } from "@/lib/homeAgents";
import { AssistantBodyPanel } from "./AssistantBodyPanel";
import { CardStream } from "./CardStream";

type CollectionShowcaseProps = {
  /** Session-pending gate from HomePage — card data itself is fetched here. */
  loading: boolean;
  authenticated: boolean;
  onSignIn: () => void;
};

type AgentProgress = { owned: number; total: number };

function AgentChip({
  agent,
  active,
  progress,
  loading,
  onSelect,
}: {
  agent: HomeAgent;
  active: boolean;
  progress: AgentProgress;
  loading: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        "flex shrink-0 snap-start items-center gap-2 rounded-full border py-2.5 pl-1.5 pr-3 transition-colors duration-200",
        active
          ? "border-accent/60 bg-accent/10"
          : "border-line bg-white/4 hover:border-line-hi",
      )}
    >
      <span
        className={cn(
          "size-6 shrink-0 overflow-hidden rounded-full border",
          active ? "border-accent/50" : "border-line",
        )}
      >
        <AgentAvatar agent={agent} className="size-full" />
      </span>
      <span
        className={cn(
          "font-mono text-[11px] font-medium uppercase tracking-[0.08em]",
          active ? "text-ink" : "text-ink-mute",
        )}
      >
        {agent.name}
      </span>
      <span className="font-mono text-[10px] font-medium tabular-nums text-ink-mute">
        {loading ? "—" : `${progress.owned}/${progress.total}`}
      </span>
    </button>
  );
}

function RailItem({
  agent,
  active,
  progress,
  loading,
  onSelect,
}: {
  agent: HomeAgent;
  active: boolean;
  progress: AgentProgress;
  loading: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={`${agent.name} — ${progress.owned} of ${progress.total} collected`}
      onClick={onSelect}
      className="group relative flex w-full flex-col items-center gap-1 px-2 py-2.5"
    >
      {/* 활성 인디케이터 — 레일 좌측 액센트 바 */}
      <span
        aria-hidden
        className={cn(
          "absolute left-0 top-1/2 h-9 w-0.5 -translate-y-1/2 rounded-full transition-colors duration-200",
          active ? "bg-accent" : "bg-transparent",
        )}
      />
      <span
        className={cn(
          "size-9 overflow-hidden rounded-full border transition-colors duration-200",
          active ? "border-accent/70" : "border-line group-hover:border-line-hi",
        )}
      >
        <AgentAvatar agent={agent} className="size-full" />
      </span>
      <span
        className={cn(
          "font-mono text-[9px] font-medium tabular-nums",
          active ? "text-accent" : "text-ink-mute",
        )}
      >
        {loading ? "—" : `${progress.owned}/${progress.total}`}
      </span>
    </button>
  );
}

/**
 * Home 02 — Collection: split showcase panel. Left = the selected assistant's
 * body layer + per-agent collection progress (AssistantBodyPanel); right = the
 * vertically streaming grid of MY cards for that assistant (CardStream); the
 * six agent chips (avatar + name + x/y) switch both. Mobile stacks chips →
 * body banner → capped card stream inside the same hairline frame.
 *
 * Data: the shared useCollectionData cache (one list_agent_cards per session,
 * stale-while-revalidate) — both the per-agent progress
 * (chips/rail/AssistantBodyPanel) and the card grid (CardStream) derive from
 * that same `items` array, so the two can never disagree. A failed fetch is
 * tracked as `cardsError`, distinct from a genuinely-empty inventory, so the
 * UI never reports "0 cards" when it actually means "couldn't load" (see
 * CardStream's failedToLoad branch).
 */
export function CollectionShowcase({ loading, authenticated, onSignIn }: CollectionShowcaseProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const {
    state: cardsState,
    loading: cardsLoading,
    error: cardsErrorMessage,
    reload: handleRetry,
  } = useCollectionData();
  // 캐시된 스냅샷 유지 — RPC 실패 시 빈 배열로 덮지 않는다. "미보유"와 "로드
  // 실패"를 구분해야 실제 소장자가 카드가 사라진 것으로 오인하지 않는다.
  const items = cardsState?.items ?? null;
  const cardsError = cardsErrorMessage !== null;

  const progressByAgent = useMemo(() => {
    const map = new Map<string, AgentProgress>();
    for (const homeAgent of HOME_AGENTS) {
      const owned =
        items?.filter((item) => item.agentId === homeAgent.agentId && item.owned).length ?? 0;
      map.set(homeAgent.agentId, { owned, total: CARD_FINISHES.length });
    }
    return map;
  }, [items]);

  const busy = loading || cardsLoading;

  // Default: first assistant with owned cards, else Emma (roster head).
  // Gated on `!busy` so the auto-pick can't flip out from under the user
  // once the fetch resolves partway through — it decides once, from settled
  // data, not from whatever partial state happens to exist mid-load.
  const agent = useMemo(() => {
    const picked = HOME_AGENTS.find((candidate) => candidate.agentId === selectedId);
    if (picked) return picked;
    if (busy) return HOME_AGENTS[0];
    return (
      HOME_AGENTS.find((candidate) => (progressByAgent.get(candidate.agentId)?.owned ?? 0) > 0) ??
      HOME_AGENTS[0]
    );
  }, [selectedId, progressByAgent, busy]);

  const progress = progressByAgent.get(agent.agentId) ?? { owned: 0, total: CARD_FINISHES.length };
  // Fetch failed and nothing ever loaded — progress readouts show "—", not a
  // false "0/N" that would misrepresent a real collector as empty.
  const unresolved = busy || (cardsError && items === null);

  return (
    <section aria-label="My collection" className="flex flex-col gap-5">
      {/* v2 S5 — 섹션 제목은 눈썹 1행만(부제목 제거), 우측 수집률 카운터(n/N, tnum) */}
      <div className="flex items-baseline justify-between gap-4">
        <p className="ty-eyebrow">#4 My collection</p>
        <span className="shrink-0 font-mono text-[11px] tabular-nums text-ink-mute">
          {cardsState ? `${cardsState.ownedCount}/${cardsState.totalCount} collected` : "—"}
        </span>
      </div>

      <div className="flex flex-col overflow-hidden rounded-2xl border border-line bg-(--ty-bg-2)">
        {/* Agent switcher — mobile: horizontal chips row (lg에선 좌측 세로 레일로 대체).
            Right-edge fade hints the row scrolls — Lisa/Sophie/Olivia sit past
            the fold and the native scrollbar affordance is hidden below. */}
        <div
          aria-label="Choose an assistant"
          className={cn(
            "order-first flex snap-x gap-2 overflow-x-auto border-b border-line px-4 py-3 lg:hidden",
            "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            "[mask-image:linear-gradient(to_right,black_calc(100%-28px),transparent)]",
          )}
        >
          {HOME_AGENTS.map((candidate) => (
            <AgentChip
              key={candidate.agentId}
              agent={candidate}
              active={candidate.agentId === agent.agentId}
              progress={
                progressByAgent.get(candidate.agentId) ?? { owned: 0, total: CARD_FINISHES.length }
              }
              loading={unresolved}
              onSelect={() => setSelectedId(candidate.agentId)}
            />
          ))}
        </div>

        {/* Split panel — [세로 셀렉터 레일 | 바디 레이어 | 카드 스트림] */}
        <div className="flex flex-col lg:grid lg:h-[600px] lg:grid-cols-[4.5rem_minmax(0,6.5fr)_minmax(0,5.5fr)]">
          <div
            aria-label="Choose an assistant"
            className="hidden lg:flex lg:min-h-0 lg:flex-col lg:items-stretch lg:gap-0.5 lg:overflow-y-auto lg:border-r lg:border-line lg:py-3"
          >
            {HOME_AGENTS.map((candidate) => (
              <RailItem
                key={candidate.agentId}
                agent={candidate}
                active={candidate.agentId === agent.agentId}
                progress={
                  progressByAgent.get(candidate.agentId) ?? {
                    owned: 0,
                    total: CARD_FINISHES.length,
                  }
                }
                loading={unresolved}
                onSelect={() => setSelectedId(candidate.agentId)}
              />
            ))}
          </div>
          <AssistantBodyPanel
            agent={agent}
            owned={progress.owned}
            total={progress.total}
            loading={unresolved}
            authenticated={authenticated}
            onSignIn={onSignIn}
          />
          <div className="border-t border-line lg:min-h-0 lg:overflow-hidden lg:border-l lg:border-t-0">
            <CardStream
              agent={agent}
              items={items}
              loading={busy}
              error={cardsError}
              onSignIn={onSignIn}
              onRetry={handleRetry}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
